import { Eye } from "@untitledui/icons"
import { AnimatePresence, motion } from "motion/react"
import { useEffect, useMemo, useState } from "react"

import { useReplay } from "@/components/replay/ReplayProvider"
import type {
  AssessorObservationPayload,
  ConversationEvent,
} from "@/types/conversation"

// The hidden adversarial grading, made briefly visible. A quiet panel that
// flashes the latest assessor signal as the trainee speaks ("noted:
// minimising"). Admin-only and toggle-gated — it mirrors the Debug Events
// (npc_debug) pattern exactly: localStorage key + a CustomEvent + a storage
// listener; off by default.
//
// Firewall (invariant #1): it reads `assessor_observation` events DIRECTLY from
// the event list — never through the conversation state builder or the
// chat-thread projector, both of which ignore these events. The trainee-facing
// transcript can therefore never render a grade.
//
// Two entry points share one presentational core (`AssessorPanel`):
//   - `AssessorView` (default) reads the replay event list via `useReplay`.
//   - On the live `/chat` surface, mount `AssessorPanel` directly with the live
//     `useConversationEvents` list and `currentEventIndex = events.length - 1`.

const STORAGE_KEY = "assessor_view"
const CHANGE_EVENT = "assessor-view-changed"

function readToggle(): boolean {
  if (typeof window === "undefined") return false
  const v = localStorage.getItem(STORAGE_KEY)
  return v === "1" || v === "true"
}

// Presentational core — decoupled from the replay context so it can mount on
// the live chat surface too. `currentEventIndex` is the inclusive upper bound of
// revealed events (in replay it's the scrubber position; live it's the latest).
export function AssessorPanel({
  events,
  currentEventIndex,
}: {
  events: ConversationEvent[]
  currentEventIndex: number
}) {
  const [on, setOn] = useState<boolean>(readToggle)

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { value: boolean } | undefined
      if (detail && typeof detail.value === "boolean") setOn(detail.value)
      else setOn(readToggle())
    }
    const onStorage = () => setOn(readToggle())
    window.addEventListener(CHANGE_EVENT, handler as EventListener)
    window.addEventListener("storage", onStorage)
    return () => {
      window.removeEventListener(CHANGE_EVENT, handler as EventListener)
      window.removeEventListener("storage", onStorage)
    }
  }, [])

  // The latest assessor observation among events revealed so far (mirrors the
  // slice the chat thread uses), so the flash tracks the scrubber position.
  const latest = useMemo(() => {
    const sliced = events.slice(
      0,
      Math.min(currentEventIndex + 1, events.length),
    )
    for (let i = sliced.length - 1; i >= 0; i--) {
      if (sliced[i].type === "assessor_observation") return sliced[i]
    }
    return null
  }, [events, currentEventIndex])

  if (!on) return null

  const payload = latest?.payload as AssessorObservationPayload | undefined

  return (
    <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50/80 px-4 py-3 dark:border-gray-700 dark:bg-gray-800/50">
      <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-gray-400 uppercase dark:text-gray-500">
        <Eye className="size-3.5" />
        Assessor
      </div>
      <AnimatePresence mode="wait">
        {payload ? (
          <motion.div
            key={latest?.event_id}
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <p className="font-mono text-sm font-medium text-gray-800 dark:text-gray-200">
              {payload.label}
            </p>
            {payload.note && (
              <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                {payload.note}
              </p>
            )}
          </motion.div>
        ) : (
          <p className="text-xs text-gray-400 italic dark:text-gray-500">
            Watching…
          </p>
        )}
      </AnimatePresence>
    </div>
  )
}

// Replay entry point: feeds the replay event list + scrubber index into the core.
export default function AssessorView() {
  const { events, currentEventIndex } = useReplay()
  return <AssessorPanel events={events} currentEventIndex={currentEventIndex} />
}
